<template>
  <TContainer>
    <CardFeaturedBlogPost
      v-if="showFeatured && featured"
      :post="featured"
    />

    <div v-if="showFeatured">
      <slot name="after-feature" />
    </div>

    <div class="mx-auto grid auto-rows-fr grid-cols-1 sm:grid-cols-2 gap-8 sm:mt-8 lg:max-w-none lg:grid-cols-3">
      <BlogCard
        v-for="post in posts"
        :key="post.id"
        :image="post?.feature_image ?? ''"
        :title="post.title ?? ''"
        :category="getPrimaryTagName(post?.tags)"
        :summary="post.custom_excerpt || post.excerpt"
        :to="localePath({
          name: 'blog-slug',
          params: {
            slug: post.slug,
          },
        })"
      />
    </div>
  </TContainer>
</template>

<script setup lang="ts">
import type { PostOrPage, Tag } from '@tryghost/content-api'

const props = withDefaults(defineProps<{
  showFeatured?: boolean
  postList?: PostOrPage[]
  columns?: number
}>(), {
  columns: 2,
})

const localePath = useLocalePath()
const runtimeConfig = useRuntimeConfig()

const featured = computed(() => {
  if (!props.showFeatured || !props.postList) return null

  return props.postList[0]
})

const posts = computed(() => {
  if (!props.postList) return null

  return props.showFeatured ? props.postList.slice(1, props.postList.length) : props.postList
})

function getPrimaryTagName(tags?: Tag[]) {
  if (!tags?.length) {
    return undefined
  }

  return tags.find((tag) => !runtimeConfig.public.locales.includes(tag.slug ?? ''))?.name
}

const absoluteLocalePath = useAbsoluteLocalePath()

useSchemaOrg([
  defineWebPage({
    mainEntity: {
      '@type': 'Blog',
      'name': 'The RVezy Blog',
      'description': 'RV posting and travel tips, stories, and more.',
      'blogPost': props.postList?.map((post) => ({
        '@type': 'BlogPosting',
        'url': absoluteLocalePath({ name: 'blog-slug', params: { slug: post.slug } }),
        'headline': post.title ?? '',
        'description': post.custom_excerpt || post.excerpt,
        'datePublished': post.published_at,
        'dateModified': post.updated_at,
      })) ?? [],
    },
  }),
])
</script>
